import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { boxProgramColors } from "@/modules/boxProgrammation/components/boxProgramColors"
import { ColorCircle } from "@/modules/boxProgrammation/components/boxProgramColorSelection"
import { useUser } from "@/modules/users/hooks/useUser"
import { HustleUpPatternIcon } from "@images/svgIcons/hustleUpPatternIcon"
import { useCurrentCoach } from "@modules/coach/hooks/useProfessionalCoach"
import type {
  CoachCategoryDefaultSettingsFormFields,
  CoachCategoryInfoFormFields,
} from "@modules/coachCategories/formTypes/coachCategoryFormTypes"
import { useCoachOffers } from "@modules/coachOffers/hooks/useCoachOffers"
import { word } from "@modules/core/utils/i18n"
import { getDurationLabel, getPriorTimeLabel } from "@modules/duration/timeConverter"
import { Globe, Upload } from "lucide-react"

type CoachCategoryResumeViewProps = {
  infoValues: CoachCategoryInfoFormFields
  settingsValues: CoachCategoryDefaultSettingsFormFields
  moveToPreviousFormStep: () => void
  onSave: () => void
  isSubmitting: boolean
}

export function CoachCategoryResumeView({
  infoValues,
  settingsValues,
  moveToPreviousFormStep,
  onSave,
  isSubmitting,
}: CoachCategoryResumeViewProps) {
  const { currentCoach } = useCurrentCoach()
  const { data: coachOffers } = useCoachOffers()
  const user = useUser()

  const selectedOffers = coachOffers.filter((o) => infoValues.offerIds.includes(o.id))
  const colorHex = boxProgramColors[infoValues.color].background

  return (
    <div className="flex flex-col pb-6">
      {/* Header */}
      <div className="relative mb-5 flex h-60 rounded-b-2xl bg-surface-background">
        {/* Left: name + visibility */}
        <div className="z-1 m-auto flex w-1/2 flex-col items-center gap-2 pl-4 text-center">
          <div className="flex items-center gap-2">
            <Text fontStyle="TL" className="wrap-break-word">
              {infoValues.name}
            </Text>
            <ColorCircle $color={colorHex} $striped />
          </div>
          {infoValues.visibleToClients && (
            <div className="flex items-center gap-1.5 text-textOnSurface-default">
              <Globe size={14} />
              <Text fontStyle="BS">{word("global.visibility.visible")}</Text>
            </div>
          )}
        </div>

        {/* Right: color panel with decorative pattern + actions */}
        <div
          className="relative w-2/3 overflow-hidden rounded-br-4xl"
          style={{
            backgroundColor: colorHex,
            clipPath: "polygon(32% 0%, 100% 0%, 100% 100%, 0% 100%)",
          }}
        >
          <HustleUpPatternIcon className="absolute top-12 left-18 size-24 text-white" />
          <HustleUpPatternIcon className="absolute top-14 -right-4 size-38 text-white" />
          <HustleUpPatternIcon className="absolute -bottom-20 left-10 size-26 text-white" />

          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <Button variant="secondary" size="iconMd" className="rounded-full">
              <Upload size={18} />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6 px-5">
        {/* Description */}
        {infoValues.description && (
          <Text fontStyle="BM" className="wrap-anywhere whitespace-break-spaces text-textOnSurface-default">
            {infoValues.description}
          </Text>
        )}

        {/* Créneaux */}
        <div className="flex flex-col gap-5 rounded-4xl bg-surface-background-secondary p-6">
          <div className="flex flex-col gap-2">
            <Text fontStyle="TS">{word("coach.planning.category.resume.slots")}</Text>
            <div className="flex flex-col rounded-2xl border border-interactive-border-neutral-highlighted bg-surface-background-float px-8 py-6 text-center">
              <Text fontStyle="TS" className="font-bold text-textOnSurface-default">
                {word("coach.planning.category.resume.duration")}
              </Text>
              <Text fontStyle="TS" className="font-bold text-textOnSurface-default">
                {getDurationLabel(settingsValues.duration)}
              </Text>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-interactive-border-neutral-default" />

          {/* Réservation */}
          {(settingsValues.limitStart ?? settingsValues.limitEnd) && (
            <div className="flex flex-col gap-2 pt-2">
              <Text fontStyle="TS">{word("coach.planning.category.resume.reservation")}</Text>
              <div className="flex gap-2">
                {settingsValues.limitStart && (
                  <div className="flex grow flex-col items-center justify-center rounded-2xl border border-interactive-border-neutral-highlighted bg-surface-background-float px-8 py-6 text-center">
                    <Text fontStyle="TS" className="font-bold text-textOnSurface-default">
                      {word("coach.planning.category.resume.openingExplanation", {
                        value: getPriorTimeLabel(settingsValues.limitStart),
                      })}
                    </Text>
                  </div>
                )}
                {settingsValues.limitEnd && (
                  <div className="flex grow flex-col items-center justify-center rounded-2xl border border-interactive-border-neutral-highlighted bg-surface-background-float px-8 py-6 text-center">
                    <Text fontStyle="TS" className="font-bold text-textOnSurface-default">
                      {word("coach.planning.category.resume.closingExplanation", {
                        value: getPriorTimeLabel(settingsValues.limitEnd),
                      })}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Separator */}
          <div className="border-t border-interactive-border-neutral-default" />

          {/* Capacité d'accueil */}
          {settingsValues.participantLimit && (
            <div className="flex items-center justify-between">
              <Text fontStyle="BM">{word("coach.planning.category.resume.participantLimit.label")}</Text>
              <Text fontStyle="BM">
                {settingsValues.participantLimit}{" "}
                {word("coach.planning.category.resume.participantLimit.unit", {
                  count: settingsValues.participantLimit,
                })}
              </Text>
            </div>
          )}

          {/* Séance d'essai */}
          <div className="flex items-center justify-between">
            <Text fontStyle="BM">{word("coach.planning.category.resume.trialSession")}</Text>
            <Text fontStyle="BM">{settingsValues.trialsAccepted ? word("global.yes") : word("global.no")}</Text>
          </div>

          {/* Offres concernées */}
          {selectedOffers.length > 0 && (
            <>
              {/* Separator */}
              <div className="border-t border-interactive-border-neutral-default" />

              <div className="flex flex-col gap-3">
                <Text fontStyle="TS">{word("coach.planning.category.form.offers.label")}</Text>
                <div className="flex flex-wrap gap-2">
                  {selectedOffers.map((offer) => (
                    <div
                      key={offer.id}
                      className="rounded-full border border-interactive-border-neutral-highlighted bg-surface-background-default px-3 py-1"
                    >
                      <Text fontStyle="BS">{offer.name}</Text>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Coach profile card */}
        <div className="flex items-center justify-center gap-4 rounded-4xl bg-surface-background-float p-6">
          {currentCoach.state.profilePhoto?.url ? (
            <img
              src={currentCoach.state.profilePhoto.url}
              alt=""
              className="flex h-24 w-24 rounded-full object-cover"
            />
          ) : (
            <div className="flex size-28 items-center justify-center rounded-full bg-interactive-background-primary-faded">
              <Text fontStyle="HL" className="font-semibold text-white">
                {currentCoach.state.firstname?.[0]?.toUpperCase()}
                {currentCoach.state.lastname?.[0]?.toUpperCase()}
              </Text>
            </div>
          )}
          <div className="flex max-w-90 min-w-0 flex-col items-stretch">
            <Text fontStyle="HS" className="block truncate text-center">
              {currentCoach.state.commercialName ??
                `${currentCoach.state.firstname ?? ""} ${currentCoach.state.lastname ?? ""}`.trim()}
            </Text>
            <Text fontStyle="BL" className="block truncate text-center">
              @{user.username}
            </Text>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 mb-12 flex w-full grow flex-col gap-4 sm:flex-row">
          <Button variant="outline" size="lg" onClick={moveToPreviousFormStep} className="grow">
            {word("global.previous")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="grow"
            disabled={isSubmitting}
            onClick={onSave}
            isLoading={isSubmitting}
          >
            {word("global.save")}
          </Button>
        </div>
      </div>
    </div>
  )
}
